import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  Card,
  CardActions,
  CardHeader,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Document, Page } from 'react-pdf';

import AttachmentPreviewDialog from '~/components/AttachmentPreviewDialog';
import { CARD_SX } from '~/theme';

import { formatDate } from './contractDetailShared';
import type { ContractReview, ContractReviewAttachment } from './types';

// Mirrors the invoice attachment card (PdfPreviewWithActions): a rendered PDF
// thumbnail on top, then a CardHeader row with the PDF icon, file name, and an
// actions menu (Download). Clicking the thumbnail opens the full preview.
const AttachmentCard = ({ attachment }: { attachment: ContractReviewAttachment }) => {
  const theme = useTheme();
  const [openPreview, setOpenPreview] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const menuButtonId = `attachment-actions-button-${attachment.id}`;
  const menuId = `attachment-actions-menu-${attachment.id}`;

  const filePath = attachment.attachmentUrl;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  return (
    // No margin of its own — the grid owns the spacing — and the shared card shape.
    <Box
      sx={{
        p: 0,
        textAlign: 'center',
        color: 'text.secondary',
        ...CARD_SX,
        overflow: 'hidden',
      }}
    >
      <Card
        sx={{
          p: 0,
          boxShadow: 'none',
          border: 'none',
          '.react-pdf__Page__canvas': {
            imageRendering: 'crisp-edges',
            backgroundColor: '#eee !important',
            height: '150px !important',
            width: '-webkit-fill-available !important',
          },
          '.react-pdf__Page__textContent': {
            cursor: 'pointer',
            userSelect: 'none',
          },
          '.react-pdf__Page__textContent span': {
            cursor: 'pointer',
            userSelect: 'none',
          },
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Preview ${attachment.fileName}`}
          onClick={() => setOpenPreview(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpenPreview(true);
            }
          }}
          sx={{ cursor: 'pointer' }}
        >
          <Document file={filePath}>
            <Page
              pageNumber={1}
              height={200}
              scale={1}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </Document>
        </Box>
        <CardActions disableSpacing sx={{ p: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
          <CardHeader
            sx={{
              alignItems: 'start',
              p: theme.spacing(2),
              textAlign: 'left',
              width: '100%',
              '& .MuiCardHeader-content': { minWidth: 0 },
            }}
            avatar={<PictureAsPdfIcon sx={{ color: 'error.main' }} aria-label="files" />}
            action={
              <>
                <IconButton
                  id={menuButtonId}
                  aria-label="attachment actions"
                  aria-controls={menuOpen ? menuId : undefined}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen ? 'true' : undefined}
                  onClick={handleMenuOpen}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id={menuId}
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  disableScrollLock
                  MenuListProps={{ 'aria-labelledby': menuButtonId }}
                >
                  <MenuItem
                    component="a"
                    href={filePath}
                    download={attachment.fileName}
                    onClick={handleMenuClose}
                    disableRipple
                  >
                    <DownloadIcon sx={{ marginRight: '10px' }} />
                    Download
                  </MenuItem>
                </Menu>
              </>
            }
            title={
              <Typography
                variant="body2"
                sx={{ fontWeight: 500 }}
                noWrap
                title={attachment.fileName}
              >
                {attachment.fileName}
              </Typography>
            }
            subheader={
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {attachment.uploadedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Uploaded {formatDate(attachment.uploadedAt)}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {attachment.isPrimary ? (
                    <Chip size="small" color="primary" label="Primary" />
                  ) : null}
                  {attachment.isSummarySheet ? (
                    <Chip size="small" color="info" label="Summary sheet" />
                  ) : null}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={attachment.hasTextLayer ? 'success' : 'warning'}
                    label={attachment.hasTextLayer ? 'Text layer' : 'Needs OCR'}
                  />
                </Stack>
              </Stack>
            }
          />
        </CardActions>
      </Card>
      <AttachmentPreviewDialog
        open={openPreview}
        filePath={filePath}
        onClose={() => setOpenPreview(false)}
      />
    </Box>
  );
};

type Props = {
  contract: ContractReview;
};

/** Card bounds, matching the invoice Attachments tab. A page preview reads at roughly this size; wider is just stretched. */
const CARD_MIN_WIDTH = 220;
const CARD_MAX_WIDTH = 300;

/** Auto-fill rather than a fixed column count: the tab fits as many cards per row as its width allows, and each keeps a page's proportions. */
const gridSx = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, ${CARD_MAX_WIDTH}px))`,
  gap: 2,
} as const;

export const ContractAttachmentsTab = ({ contract }: Props) => {
  const { attachments } = contract;
  const uploads = attachments.filter((a) => a.kind !== 'cfi_report');
  const cfiReports = attachments.filter((a) => a.kind === 'cfi_report');

  return (
    <Box sx={{ flexGrow: 1, pb: 6, p: 2 }}>
      {uploads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No uploaded documents for this contract.
        </Typography>
      ) : (
        <Box sx={gridSx}>
          {uploads.map((attachment) => (
            <AttachmentCard key={attachment.id} attachment={attachment} />
          ))}
        </Box>
      )}

      {cfiReports.length > 0 ? (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            Contract Analysis (ContractIQ)
          </Typography>
          <Box sx={gridSx}>
            {cfiReports.map((attachment) => (
              <AttachmentCard key={attachment.id} attachment={attachment} />
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  );
};
